/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DateTime } from 'luxon';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import syntaxTheme from 'react-syntax-highlighter/dist/esm/styles/hljs/github-gist';

import ApplyIcon from '@mui/icons-material/Check';
import CopyIcon from '@mui/icons-material/ContentCopy';
import SchoolIcon from '@mui/icons-material/School';
import { Alert, Chip, Divider, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';

import { AdviceSeverityColor } from 'shared';

import { CopyToClipboard } from '../../../../../components/CopyToClipboard/component';
import { HUModal2 } from '../../../../../components/HUModal2/HUModal2';
import { createAdviceTabStyles } from '../AdviceTab.styles';
import { Advice } from '../AdviceTab.types';

SyntaxHighlighter.registerLanguage('sql', sql);

type Props = {
  open: boolean;
  onClose: () => void;
  advice: Advice | null;
};

export const AdviceInfo = memo<Props>(props => {
  const { t } = useTranslation();
  const classes = createAdviceTabStyles();

  return (
    <HUModal2
      open={props.open}
      onCancel={props.onClose}
      size="large"
      cancelButtonText={t('cluster:advice.info.cancelButton')}
    >
      {props.advice ? (
        <Stack spacing={2} m={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h2">{t('cluster:advice.info.title')}</Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                style={{ backgroundColor: AdviceSeverityColor[props.advice.severity] }}
                label={t(`cluster:advice.severities.${props.advice.severity}`)}
              />
              {props.advice.script ? (
                <Tooltip arrow title={t('cluster:advice.info.scriptAvailableTooltip')}>
                  <Chip icon={<ApplyIcon />} label={t('cluster:advice.info.scriptAvailable')} color="primary" />
                </Tooltip>
              ) : null}
              {props.advice.furtherReading ? (
                <Tooltip arrow title={t('cluster:advice.info.furtherReadingAvailableTooltip')}>
                  <Chip
                    icon={<SchoolIcon />}
                    label={t('cluster:advice.info.furtherReadingAvailable')}
                    color="primary"
                  />
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={4}>
            <Stack>
              <Typography className={classes.infoBoxLabel}>{t('cluster:advice.labels.type')}</Typography>
              <Typography className={classes.infoBoxValue} noWrap>
                {t(`cluster:advice.modules.${props.advice.type}`)}
              </Typography>
            </Stack>
            <Stack overflow="scroll">
              <Typography className={classes.infoBoxLabel}>{t('cluster:advice.labels.object')}</Typography>
              <Typography className={classes.infoBoxValue} fontFamily="monospace" overflow="scroll">
                {props.advice.object || 'N/A'}
              </Typography>
            </Stack>
            <Stack overflow="scroll">
              <Typography className={classes.infoBoxLabel}>{t('cluster:advice.labels.subobject')}</Typography>
              <Typography className={classes.infoBoxValue} fontFamily="monospace" overflow="scroll">
                {props.advice.subObject || 'N/A'}
              </Typography>
            </Stack>
            <Stack>
              <Typography className={classes.infoBoxLabel}>{t('cluster:advice.labels.createdAt')}</Typography>
              <Tooltip title={DateTime.fromISO(props.advice.run.timestamp).toLocaleString(DateTime.DATETIME_FULL)}>
                <Typography className={classes.infoBoxValue} noWrap>
                  {DateTime.fromISO(props.advice.run.timestamp).toRelative()}
                </Typography>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider orientation="horizontal" />

          <Stack>
            <Typography>
              <b>{t('cluster:advice.labels.info')}</b>
            </Typography>
            <Typography variant="body2">{props.advice.info}</Typography>
          </Stack>

          {props.advice.text ? (
            <Stack>
              <Typography>
                <b>{t('cluster:advice.labels.text')}</b>
              </Typography>
              <Typography variant="body2">{props.advice.text}</Typography>
            </Stack>
          ) : null}

          {props.advice.detail ? (
            <Stack>
              <Typography>
                <b>{t('cluster:advice.labels.detail')}</b>
              </Typography>
              <Typography variant="body2">{props.advice.detail}</Typography>
            </Stack>
          ) : null}

          {props.advice.script ? (
            <Stack>
              <Stack direction="row" alignItems="center">
                <Typography>
                  <b>{t('cluster:advice.labels.script')}</b>
                </Typography>
                <CopyToClipboard text={props.advice.script}>
                  <IconButton>
                    <CopyIcon style={{ width: '16px', height: '16px' }} />
                  </IconButton>
                </CopyToClipboard>
              </Stack>

              <SyntaxHighlighter
                customStyle={{ fontSize: '13px', padding: 0, margin: 0 }}
                language="sql"
                style={syntaxTheme}
              >
                {props.advice.script}
              </SyntaxHighlighter>
            </Stack>
          ) : null}

          {props.advice.furtherReading ? (
            <Stack>
              <Typography>
                <b>{t('cluster:advice.labels.furtherReading')}</b>
              </Typography>
              <Link variant="body2" href={props.advice.furtherReading} underline="hover" target="_blank" rel="noopener">
                {props.advice.furtherReading}
              </Link>
            </Stack>
          ) : null}
        </Stack>
      ) : (
        <Alert severity="error">{t('common:errors.unexpected_error')}</Alert>
      )}
    </HUModal2>
  );
});
